import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Button,
  Card,
  Space,
  message,
  Row,
  Col,
  Typography,
  Descriptions,
  Table,
  Divider,
  Tag,
  Spin,
  Select,
} from 'antd';
import {
  PlusOutlined,
  MinusCircleOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import axios from 'axios';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface DiagnosticReport {
  id: number;
  intervention_id: number;
  problem_description: string;
  recommended_solution: string;
  parts_needed: Array<{
    name: string;
    quantity: number;
    unit_price: number;
  }>;
  labor_cost: number;
  estimated_total: number;
  urgency_level: string;
  estimated_duration: string;
  intervention?: any;
  technician?: any;
}

const CreateQuoteFromReportPage: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState<DiagnosticReport | null>(null);
  const [lineItems, setLineItems] = useState<any[]>([]);
  const [tvaRate, setTvaRate] = useState<number>(0); // 0%, 18%, etc.

  useEffect(() => {
    if (reportId) {
      fetchReportDetails();
    }
  }, [reportId]);

  const fetchReportDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/diagnostic-reports/${reportId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const reportData = response.data;
      setReport(reportData);

      // Parser parts_needed si c'est un string JSON
      let partsNeeded = reportData.parts_needed;
      if (typeof partsNeeded === 'string') {
        try {
          partsNeeded = JSON.parse(partsNeeded);
        } catch (e) {
          partsNeeded = [];
        }
      }
      if (!Array.isArray(partsNeeded)) {
        partsNeeded = [];
      }

      // Pré-remplir les lignes du devis avec les pièces + main d'œuvre
      const initialLineItems = [
        ...partsNeeded.map((part: any) => ({
          description: part.name,
          quantity: part.quantity,
          unit_price: part.unit_price || 0,
          total: part.quantity * (part.unit_price || 0),
          unit: 'pcs',
        })),
        {
          description: "Main d'œuvre - " + reportData.recommended_solution.substring(0, 50),
          quantity: 1,
          unit_price: reportData.labor_cost || 0,
          total: reportData.labor_cost || 0,
          unit: 'ens',
        },
      ];

      setLineItems(initialLineItems);

      // Calculer les montants
      const subtotal = initialLineItems.reduce((sum, item) => sum + item.total, 0);
      form.setFieldsValue({
        subtotal,
        taxAmount: 0,
        discountAmount: 0,
        total: subtotal,
        expiryDays: 7,
        objet: "Devis d'entretien préventif de la climatisation",
        termsAndConditions:
          'Paiement avant intervention. Garantie 6 mois sur les pièces et la main d\'œuvre.',
      });
    } catch (error: any) {
      message.error('Erreur lors du chargement du rapport');
      console.error('Error fetching report:', error);
      navigate('/diagnostic-reports');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLineItem = () => {
    setLineItems([
      ...lineItems,
      { description: '', quantity: 1, unit_price: 0, total: 0, unit: 'ens' },
    ]);
  };

  const handleRemoveLineItem = (index: number) => {
    const newItems = lineItems.filter((_, i) => i !== index);
    setLineItems(newItems);
    recalculateTotals(newItems);
  };

  const handleLineItemChange = (index: number, field: string, value: any) => {
    const newItems = [...lineItems];
    newItems[index][field] = value;

    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total =
        (newItems[index].quantity || 0) * (newItems[index].unit_price || 0);
    }

    setLineItems(newItems);
    recalculateTotals(newItems, tvaRate);
  };

  const recalculateTotals = (items: any[], currentTvaRate?: number) => {
    const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
    const rate = currentTvaRate !== undefined ? currentTvaRate : tvaRate;
    const taxAmount = Math.round(subtotal * rate / 100);
    const discountAmount = form.getFieldValue('discountAmount') || 0;
    const total = subtotal + taxAmount - discountAmount;

    form.setFieldsValue({
      subtotal,
      taxAmount,
      total,
    });
  };

  const handleTvaRateChange = (rate: number) => {
    setTvaRate(rate);
    recalculateTotals(lineItems, rate);
  };

  const handleDiscountChange = () => {
    const subtotal = form.getFieldValue('subtotal') || 0;
    const taxAmount = form.getFieldValue('taxAmount') || 0;
    const discountAmount = form.getFieldValue('discountAmount') || 0;
    const total = subtotal + taxAmount - discountAmount;

    form.setFieldsValue({ total });
  };

  const handleSubmit = async (values: any) => {
    if (lineItems.length === 0) {
      message.error('Veuillez ajouter au moins une ligne au devis');
      return;
    }

    // Vérifier que toutes les lignes sont remplies
    const invalidItems = lineItems.filter(
      (item) =>
        !item.description ||
        item.description.trim() === '' ||
        !item.quantity ||
        !item.unit_price ||
        !item.unit ||
        item.unit.trim() === ''
    );

    if (invalidItems.length > 0) {
      message.error('Veuillez remplir tous les champs (y compris l\'unité) des lignes du devis');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/quotes/from-report`,
        {
          diagnostic_report_id: Number(reportId),
          line_items: lineItems,
          subtotal: values.subtotal,
          taxAmount: values.taxAmount || 0,
          discountAmount: values.discountAmount || 0,
          total: values.total,
          notes: values.notes,
          termsAndConditions: values.termsAndConditions,
          expiryDays: values.expiryDays || 7,
          objet: values.objet,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      message.success('Devis créé et envoyé au client avec succès');
      navigate('/diagnostic-reports');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erreur lors de la création du devis');
      console.error('Error creating quote:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getUrgencyColor = (level: string) => {
    switch (level) {
      case 'low':
        return 'green';
      case 'medium':
        return 'orange';
      case 'high':
        return 'red';
      case 'critical':
        return 'purple';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!report) {
    return null;
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space style={{ marginBottom: 24 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/diagnostic-reports')}>
            Retour
          </Button>
        </Space>

        <Title level={3}>Créer un Devis à partir du Rapport #{reportId}</Title>

        {/* Rapport info */}
        <Card
          title="Informations du Rapport de Diagnostic"
          style={{ marginBottom: 24 }}
          size="small"
        >
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Intervention">
              #{report.intervention_id}
            </Descriptions.Item>
            <Descriptions.Item label="Technicien">
              {report.technician
                ? `${report.technician.firstName} ${report.technician.lastName}`
                : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Urgence">
              <Tag color={getUrgencyColor(report.urgency_level)}>
                {report.urgency_level.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Durée estimée">
              {report.estimated_duration}
            </Descriptions.Item>
            <Descriptions.Item label="Problème" span={2}>
              {report.problem_description}
            </Descriptions.Item>
            <Descriptions.Item label="Solution recommandée" span={2}>
              {report.recommended_solution}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Form */}
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Card title="Détails Généraux du Devis" style={{ marginBottom: 24 }}>
            <Form.Item
              label="Objet du Devis"
              name="objet"
              rules={[{ required: true, message: "Veuillez saisir l'objet du devis" }]}
            >
              <Input placeholder="ex: Devis d'entretien préventif de la climatisation" />
            </Form.Item>
          </Card>

          <Card title="Lignes du Devis" style={{ marginBottom: 24 }}>
            <Table
              dataSource={lineItems}
              rowKey={(record, index) => index?.toString() || '0'}
              columns={[
                {
                  title: 'Description',
                  key: 'description',
                  render: (record, _, index) => (
                    <Input
                      placeholder="Description de la ligne"
                      value={record.description}
                      onChange={(e) =>
                        handleLineItemChange(index, 'description', e.target.value)
                      }
                    />
                  ),
                },
                {
                  title: 'Unité (U.)',
                  key: 'unit',
                  width: 100,
                  render: (record, _, index) => (
                    <Input
                      placeholder="ex: ens, pcs"
                      value={record.unit}
                      onChange={(e) =>
                        handleLineItemChange(index, 'unit', e.target.value)
                      }
                    />
                  ),
                },
                {
                  title: 'Quantité',
                  key: 'quantity',
                  width: 120,
                  render: (record, _, index) => (
                    <InputNumber
                      min={1}
                      value={record.quantity}
                      onChange={(value) => handleLineItemChange(index, 'quantity', value)}
                      style={{ width: '100%' }}
                    />
                  ),
                },
                {
                  title: 'Prix unitaire (FCFA)',
                  key: 'unit_price',
                  width: 150,
                  render: (record, _, index) => (
                    <InputNumber
                      min={0}
                      value={record.unit_price}
                      onChange={(value) => handleLineItemChange(index, 'unit_price', value)}
                      style={{ width: '100%' }}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                    />
                  ),
                },
                {
                  title: 'Total (FCFA)',
                  key: 'total',
                  width: 150,
                  render: (_, record) => (
                    <Text strong>{Number(record.total || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}</Text>
                  ),
                },
                {
                  title: 'Action',
                  key: 'action',
                  width: 80,
                  render: (_, __, index) => (
                    <Button
                      type="link"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => handleRemoveLineItem(index)}
                      disabled={lineItems.length === 1}
                    />
                  ),
                },
              ]}
              pagination={false}
              size="small"
            />

            <Button
              type="dashed"
              onClick={handleAddLineItem}
              block
              icon={<PlusOutlined />}
              style={{ marginTop: 16 }}
            >
              Ajouter une ligne
            </Button>
          </Card>

          <Card title="Calculs" style={{ marginBottom: 24 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="Sous-total (FCFA)" name="subtotal">
                  <InputNumber
                    disabled
                    style={{ width: '100%' }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Taux TVA">
                  <Select
                    value={tvaRate}
                    onChange={handleTvaRateChange}
                    style={{ width: '100%' }}
                  >
                    <Option value={0}>0% (Exonéré)</Option>
                    <Option value={9}>9%</Option>
                    <Option value={18}>18%</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="TVA (FCFA)" name="taxAmount">
                  <InputNumber
                    disabled
                    style={{ width: '100%' }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Remise (FCFA)" name="discountAmount">
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    onChange={handleDiscountChange}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Row justify="end">
              <Col>
                <Form.Item label="Total (FCFA)" name="total">
                  <InputNumber
                    disabled
                    style={{ width: '100%', fontSize: 24, fontWeight: 'bold' }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card title="Informations complémentaires" style={{ marginBottom: 24 }}>
            <Form.Item label="Notes" name="notes">
              <TextArea
                rows={3}
                placeholder="Informations complémentaires sur le devis..."
              />
            </Form.Item>

            <Form.Item
              label="Conditions générales"
              name="termsAndConditions"
              rules={[{ required: true, message: 'Ce champ est obligatoire' }]}
            >
              <TextArea rows={3} placeholder="Conditions générales du devis..." />
            </Form.Item>

            <Form.Item
              label="Validité du devis (jours)"
              name="expiryDays"
              rules={[{ required: true, message: 'Ce champ est obligatoire' }]}
            >
              <InputNumber min={1} max={90} style={{ width: '100%' }} />
            </Form.Item>
          </Card>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                icon={<SaveOutlined />}
                size="large"
              >
                Créer et envoyer le devis au client
              </Button>
              <Button onClick={() => navigate('/diagnostic-reports')} size="large">
                Annuler
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CreateQuoteFromReportPage;
